import type { PublicEvent, PublicEventDetail } from "@homesite/api-contract";
import { queryKeys } from "@homesite/queries";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useApiClient } from "../lib/ApiClientContext";
import { useDebouncedValue } from "../search/useDebouncedValue";
import type { PaginationInfo } from "./useLoanTimeline";

export type SearchType = "event" | "loan";

export interface EventSearchFilters {
	eventType?: string;
	respaCode?: string;
}

export interface EventSearchData {
	searchValue: string;
	onSearchChange: (value: string) => void;
	searchType: SearchType;
	onSearchTypeChange: (type: SearchType) => void;
	filters: EventSearchFilters;
	onFilterChange: (key: keyof EventSearchFilters, value: string) => void;
	onRemoveFilter: (key: keyof EventSearchFilters) => void;
	onClearFilters: () => void;
	results: PublicEvent[] | PublicEventDetail[];
	pagination: PaginationInfo | null;
	onPageChange: (page: number) => void;
	loading: boolean;
	error: Error | null;
}

/**
 * Search hook for the event search page. Debounces the user's query by 300ms
 * then dispatches the appropriate endpoint based on searchType:
 *   - "event"  → getEventByHash (by exact hash)
 *   - "loan"   → getLoanTimeline (by loan ID, paginated)
 *
 * All search state is local. For URL-persisted state use useEventFilters.
 */
export function useEventSearch(
	initialSearchType: SearchType = "loan",
): EventSearchData {
	const client = useApiClient();

	const [searchValue, setSearchValue] = useState("");
	const [searchType, setSearchType] = useState<SearchType>(initialSearchType);
	const [filters, setFilters] = useState<EventSearchFilters>({});
	const [page, setPage] = useState(1);

	const debouncedSearch = useDebouncedValue(searchValue, 300);

	const eventQuery = useQuery({
		queryKey: queryKeys.publicVerification.events.byHash(debouncedSearch),
		queryFn: async () => {
			const result = await client.publicVerification.getEventByHash({
				params: { hash: debouncedSearch },
			});
			if (result.status === 200) {
				return result.body;
			}
			throw new Error("Event not found");
		},
		enabled: searchType === "event" && debouncedSearch.length > 0,
	});

	const loanQuery = useQuery({
		queryKey: queryKeys.publicVerification.loans.timeline(
			debouncedSearch,
			page,
			20,
			{ eventType: filters.eventType, respaCode: filters.respaCode },
		),
		queryFn: async () => {
			const result = await client.publicVerification.getLoanTimeline({
				params: { loanId: debouncedSearch },
				query: {
					page,
					limit: 20,
					eventType: filters.eventType,
					respaCode: filters.respaCode as
						| "a"
						| "b"
						| "c"
						| "d"
						| "e"
						| "f"
						| "g"
						| "h"
						| "i"
						| "j"
						| "k"
						| "l"
						| "m"
						| undefined,
				},
			});
			if (result.status === 200) {
				return result.body;
			}
			throw new Error("Loan not found");
		},
		enabled: searchType === "loan" && debouncedSearch.length > 0,
	});

	const isEventSearch = searchType === "event";
	const activeQuery = isEventSearch ? eventQuery : loanQuery;

	const results: PublicEvent[] | PublicEventDetail[] = isEventSearch
		? eventQuery.data
			? [eventQuery.data]
			: []
		: (loanQuery.data?.items ?? []);

	const pagination: PaginationInfo | null =
		!isEventSearch && loanQuery.data
			? {
					page: loanQuery.data.page,
					limit: loanQuery.data.limit,
					total: loanQuery.data.total,
					totalPages: loanQuery.data.totalPages,
				}
			: null;

	return {
		searchValue,
		onSearchChange: (value: string) => {
			setSearchValue(value);
			setPage(1);
		},
		searchType,
		onSearchTypeChange: (type: SearchType) => {
			setSearchType(type);
			setPage(1);
		},
		filters,
		onFilterChange: (key: keyof EventSearchFilters, value: string) => {
			setFilters((prev) => ({ ...prev, [key]: value }));
			setPage(1);
		},
		onRemoveFilter: (key: keyof EventSearchFilters) => {
			setFilters((prev) => {
				const next = { ...prev };
				delete next[key];
				return next;
			});
			setPage(1);
		},
		onClearFilters: () => {
			setFilters({});
			setPage(1);
		},
		results,
		pagination,
		onPageChange: setPage,
		loading: activeQuery.isLoading,
		error: activeQuery.error,
	};
}
